import { getAds } from '@/api/ads';
import { IAdsData } from '@/api/ads/interfaces';
import { livescoreClient } from '@/api/livescore/client';
import {
  ApiCompetitionsGet200ResponseDataCompetitions,
  ApiMatchEventIdGet200ResponseDataMatch,
} from '@/api/livescore/generated';
import { getOwnFavoriteCompetitors, getOwnFavoriteMatches } from '@/api/user';
import MatchTemplate from '@/components/templates/MatchTemplate/MatchTemplate';
import { LivescoreLayoutProvider } from '@/context/LivescoreLayoutContext';
import { GetServerSideProps, NextPage } from 'next';
import Head from 'next/head';

export interface MatchPageProps {
  match: ApiMatchEventIdGet200ResponseDataMatch;
  adsSidebar: IAdsData;
  adsMatch: IAdsData;
  competitions: ApiCompetitionsGet200ResponseDataCompetitions;
  favoriteMatches: string[];
  favoriteCompetitors: string[];
}

export const getServerSideProps: GetServerSideProps<MatchPageProps> = async (context) => {
  const authData = context.req.cookies['authData'];

  let favoriteMatches = [];
  let favoriteCompetitors = [];

  if (authData) {
    try {
      const authDataJson = JSON.parse(authData);
      const token = authDataJson.token;

      if (token) {
        const [favoriteMatchesData, favoriteCompetitorsData] = await Promise.all([
          getOwnFavoriteMatches(token),
          getOwnFavoriteCompetitors(token),
        ]);

        if (favoriteMatchesData.favoriteMatches) {
          favoriteMatches = favoriteMatchesData.favoriteMatches;
        }

        if (favoriteCompetitorsData.favoriteCompetitors) {
          favoriteCompetitors = favoriteCompetitorsData.favoriteCompetitors;
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  const [matchResponse, competitionsResponse, adsSidebar, adsMatch] = await Promise.all([
    livescoreClient.apiMatchEventIdGet({
      eventId: context.params?.id as string,
    }),
    livescoreClient.apiCompetitionsGet(),
    getAds(['Livescore']),
    getAds(['LivescoreMatch']),
  ]);

  if (!matchResponse.data.data.match) {
    console.log('not found');
    return {
      notFound: true,
    };
  }

  const match = matchResponse.data.data.match;

  if (match.sport_event.start_time) {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);

    const eventStart = new Date(match.sport_event.start_time);

    if (eventStart.getTime() < weekAgo.getTime()) {
      context.res.statusCode = 410;
    }
  }

  const competitions = competitionsResponse.data.data.competitions;

  return {
    props: {
      match,
      adsSidebar,
      adsMatch,
      competitions,
      favoriteMatches,
      favoriteCompetitors,
    },
  };
};

export const buildMatchPageTitle = (match: ApiMatchEventIdGet200ResponseDataMatch) => {
  const homeCompetitor = match?.sport_event?.competitors?.find((c) => c.qualifier === 'home');
  const awayCompetitor = match?.sport_event?.competitors?.find((c) => c.qualifier === 'away');

  const abb = `${homeCompetitor?.abbreviation} ${match?.sport_event_status?.home_score ?? ''}-${
    match?.sport_event_status?.away_score ?? ''
  } ${awayCompetitor?.abbreviation}`;

  const date = new Date(match?.sport_event?.start_time ?? '');
  const formattedDate =
    String(date.getDate()).padStart(2, '0') +
    '/' +
    String(date.getMonth() + 1).padStart(2, '0') +
    '/' +
    date.getFullYear();

  const names = `${homeCompetitor?.name} - ${awayCompetitor?.name} ${
    match?.sport_event?.start_time ? formattedDate : ''
  }`;

  const closedStatuses = ['closed', 'ended', 'abandoned', 'cancelled'];

  const endfix = closedStatuses.includes(match?.sport_event_status?.status)
    ? 'Wyniki i statystyki'
    : 'NA ŻYWO, H2H, składy';

  return `${abb} | ${names} | ${endfix}`;
};

export const buildMatchPageDescription = (match: ApiMatchEventIdGet200ResponseDataMatch) => {
  const upcomingStatuses = ['not_started', 'postponed', 'delayed'];

  const homeName = match?.sport_event?.competitors?.find((c) => c.qualifier === 'home')?.name;
  const awayName = match?.sport_event?.competitors?.find((c) => c.qualifier === 'away')?.name;
  const result = `${match?.sport_event_status?.home_score} - ${match?.sport_event_status?.away_score}`;
  const date = match?.sport_event?.start_time
    ? ` ${new Date(match?.sport_event?.start_time).toLocaleDateString()}`
    : '';

  if (upcomingStatuses.includes(match?.sport_event_status?.status)) {
    return `Śledź mecz ${homeName} - ${awayName} na żywo! Sprawdź aktualne składy, statystyki H2H, formę zespołów i przewidywania przed spotkaniem${date}. Bądź na bieżąco z Fangol!`;
  }

  return `Wynik meczu ${homeName} - ${awayName} (${result}). Zobacz szczegółowe statystyki, strzelców bramek, posiadanie piłki i podsumowanie spotkania${
    date ? ` z dnia${date}` : ''
  }. Wszystko w jednym miejscu.`;
};

const MatchPage: NextPage<MatchPageProps> = ({
  match,
  adsSidebar,
  adsMatch,
  competitions,
  favoriteMatches,
  favoriteCompetitors,
}) => {
  if (!match) {
    return <p>test</p>;
  }
  return (
    <LivescoreLayoutProvider
      competitions={competitions}
      favoriteMatchesInit={favoriteMatches}
      favoriteCompetitorsInit={favoriteCompetitors}
    >
      <Head>
        <title>{buildMatchPageTitle(match)}</title>
        <meta name="description" content={buildMatchPageDescription(match)} />
      </Head>
      <MatchTemplate match={match} adsSidebar={adsSidebar} adsMatch={adsMatch} />
    </LivescoreLayoutProvider>
  );
};

export default MatchPage;
